import {ClientUserAssignment, RiskFlags, Roster, User} from "@/api/dto/dto";
import {NotificationFrequencyType} from "@/api/dto/notification";
import {StateConfig} from "@/api/dto/state-config";
import {CrisisStatus} from "@clients/profile_page/summary_tab/timeline_components/CrisisCard.constants";
import {
  displayValueToGroupTypes,
  GroupTypes,
  Status
} from "@clients/profile_page/summary_tab/timeline_components/ADTCard.constants";
import {ReferralStatus} from "@clients/profile_page/summary_tab/timeline_components/ReferralCard.constants";
import {SDOHStatus} from "@clients/profile_page/summary_tab/timeline_components/SDOHCard.constants";
import {CareJourneyResponse} from "@/api/dto/care-journey";

export enum JobStatus {
  Complete = "COMPLETE",
  InProgress = "IN_PROGRESS",
  Retrying = "RETRYING",
  Failed = "FAILED",
}

export interface JobResult {
  jobId: string;
  apiResultStatusCode?: number;
  status: JobStatus;
  data?: string;
}

export enum EncounterType {
  ADT = "ADT",
  OPENBEDS_REFERRAL = "OPENBEDS_REFERRAL",
  OPENBEDS_CRISIS = "OPENBEDS_CRISIS",
  JUSTICE_INVOLVED = "JUSTICE_INVOLVED",
  FINDHELP_SDOH = "FINDHELP_SDOH",
}

type StatusOption = {
  value: string;
  label: string;
};

const AdtStatuses : StatusOption[] =
    Object.entries(Status)
      .filter(([key]) => key !== 'Unknown')
      .filter(([_, value]) => value.display)
      .map(([key, _]) => ({value: key, label: key}));

const crisisStatuses : StatusOption[] =
    Object.entries(CrisisStatus)
      .filter(([key]) => key !== 'UNKNOWN')
      .map(([key, value]) => ({value: key, label: value.displayName}));

const openBedsReferralStatuses : StatusOption[] =
    Object.entries(ReferralStatus)
      .filter(([key]) => key !== 'Unknown' && key !== 'AcceptedElsewhere')
      .map(([key, value]) => ({value: key, label: value.displayName}));

const sdohStatuses : StatusOption[] =
    Object.entries(SDOHStatus)
      .map(([key, value]) => ({value: key, label: value.displayName}));

const jiStatuses: StatusOption[] = [
  {value: 'Released', label: 'Released'},
  {value: 'Booked', label: 'Booked'}
];

export type EventFilterPermissions = {
  jiEnabled: boolean;
  openBedsReferralEnabled: boolean;
  openBedsCrisisEnabled: boolean;
  findHelpSdohEnabled: boolean;
  adtEnabled: boolean;
};

export const getEventFilterOptionLabels =
    (stateConfig: StateConfig, encounterType: EncounterType | keyof typeof GroupTypes): string => {
      switch (encounterType) {
      case EncounterType.JUSTICE_INVOLVED:
        return 'Justice Involved';
      case EncounterType.OPENBEDS_REFERRAL:
        return stateConfig?.branding.openBeds.referral.service;
      case EncounterType.OPENBEDS_CRISIS:
        return stateConfig?.branding.openBeds.crisis.service;
      case EncounterType.FINDHELP_SDOH:
        return stateConfig?.branding.findHelp.service;
      default:
        return 'Unknown';
      }
    };

const AdtTypes = Object.entries(GroupTypes)
  .filter(([key]) => key !== 'Unknown')
  .reduce((acc, [key, value]) => {
    const groupLabel = value.displayName;
    const groupValue: string =
         displayValueToGroupTypes.POST_ACUTE.includes(key) 
           ? 'POST_ACUTE'
           : key;

    const labelExists = Object.values(acc).some(item => item.label === value.displayName);
    if (!labelExists) {
      acc[key] = {label: groupLabel, value: groupValue, statuses: AdtStatuses};
    }
    return acc;
  }, {} as Record<EncounterType | keyof typeof GroupTypes | keyof typeof displayValueToGroupTypes,
      {label: string, value: string, statuses: StatusOption[]}>);

export const EventFilterOptions = (
  stateConfig: StateConfig,
  permissions: EventFilterPermissions
) => {
  const options: Record<EncounterType | keyof typeof GroupTypes, {label: string, value: string, statuses: StatusOption[]}> = {};

  if (permissions.jiEnabled) {
    options.JUSTICE_INVOLVED = {
      label: getEventFilterOptionLabels(stateConfig, EncounterType.JUSTICE_INVOLVED),
      value: EncounterType.JUSTICE_INVOLVED,
      statuses: jiStatuses
    };
  }

  if (permissions.openBedsReferralEnabled) {
    options.OPENBEDS_REFERRAL = {
      label: getEventFilterOptionLabels(stateConfig, EncounterType.OPENBEDS_REFERRAL),
      value: EncounterType.OPENBEDS_REFERRAL,
      statuses: openBedsReferralStatuses
    };
  }

  if (permissions.openBedsCrisisEnabled) {
    options.OPENBEDS_CRISIS = {
      label: getEventFilterOptionLabels(stateConfig, EncounterType.OPENBEDS_CRISIS),
      value: EncounterType.OPENBEDS_CRISIS,
      statuses: crisisStatuses
    };
  }

  if (permissions.findHelpSdohEnabled) {
    options.FINDHELP_SDOH = {
      label: getEventFilterOptionLabels(stateConfig, EncounterType.FINDHELP_SDOH),
      value: EncounterType.FINDHELP_SDOH,
      statuses: sdohStatuses
    };
  }

  if (permissions.adtEnabled) {
    Object.assign(options, AdtTypes);
  }

  return options;
};

export interface Client {
  publicPatientId: string;
  patientId?: string;
  firstName?: string;
  middleName?: string;
  lastName?: string;
  dateOfBirth?: string;
  gender?: string;
  phone?: string;
  address?: Address;
  ssn?: string;
  roster: Roster;
  notifications?: Notification[];
  userAssignments?: ClientUserAssignment[];
}

export type InsurancePlan = {
  id: number;
  insurerId: number;
  rosterPatientId?: number;
  canceled: boolean;
  code: string;
  insuranceCompanyName: string;
  insuranceTranslatedName?: string;
  value: string;
  selected: boolean;
  insurancePlanName?: string;
  insurancePlanOrder?: number;
  type?: string;
};

export interface Notification {
  id: number;
  user: User;
  client: Client;
  groupId: number;
  eventId: number;
  viewed: boolean;
  metadata?: string;
  canceled: boolean;
  notificationType: string;
  notificationSubtype: string;
  eventDate: string;
  externalNotifications: ExternalNotification[];
}

export interface ExternalNotification {
  id: number;
  notification: Notification;
  sent: boolean;
  method: string;
  notificationFrequency: NotificationFrequencyType;
}

export interface Address {
  address1: string;
  address2?: string;
  address3?: string;
  city: string;
  state: string;
  zip: string;
}

export interface ExtendedDemographics {
  ethnicity?: string;
  race?: string;
  maritalStatus?: string;
  language?: string;
  veteranStatus?: string;
  nextOfKin?: NextOfKin[];
  nameSuffix?: string;
  allergies?: Allergy[];
}

export interface Allergy {
  code?: string;
  description?: string;
  reaction?: string;
}

export interface ReportedBy {
  groupId: number;
  groupName?: string;
}

export interface NextOfKin {
  firstName: string;
  lastName: string;
  relationship: string;
  phoneNumbers: string[];
  email?: string;
  reportedBy?: ReportedBy;
}

export interface Patient {
  patientId: string;
  firstName: string;
  middleName: string;
  lastName: string;
  dateOfBirth: string;
  gender: string;
  phone: string;
  address: Address;
  ssn: string;
  unrestrictedPatientFlags : RiskFlags;
  creatorGroupId: number;
  creatorGroupName: string;
  clientId: string;
}

export interface MatchedPatient {
  patientId: string;
  groupId: number;
}

export interface PatientPhoneNumber {
  groupId: number;
  groupName: string;
  groupType: string;
  phone: string;
  type: string;
  updatedDate: string;
  currentOrganization: boolean;
}

export interface PatientEncounterInsurance {
  groupId: number;
  groupName: string;
  groupType: string;
  encounterStartDate: string;
  encounterEndDate: string;
  insurances: Insurance[];
}

export interface PatientRosterInsurance {
  groupId: number;
  groupName: string;
  uploadDate: string;
  insurances: string[];
}

export interface Insurance {
  insuranceCompanyName: string;
  value: string;
  subscriberId: string;
  relationshipToSubscriber: string;
}

export interface Diagnosis {
  fullCode?: string;
  synonym?: string;
}

export interface BihProcedure {
  code?: string;
  description?: string;
  codeSystemLabel?: string;
}

export interface ADTEvent {
  eventId: number,
  eventDate: string;
  payor?: {
    insuranceCompanyName?: string;
    value?: string;
  };
  setting?: string;
  status?: string;
}

export interface ClientProfile {
  patient: Patient;
  client: ClientData;
  extendedDemographics?: ExtendedDemographics;
  patientPhoneNumbers: PatientPhoneNumber[];
  /**
   * Server-computed best dialable phone (E.164) — the same number the campaign dialer selects.
   * The profile header renders this so display matches dial. Undefined when no valid number
   * exists. See CARECO-6135.
   */
  bestPhoneNumber?: string;
  patientEncounterInsurances: PatientEncounterInsurance[]
  patientRosterInsurances: PatientRosterInsurance[];
  matchedPatients?: MatchedPatient[];
  currentInsurancePlans?: InsurancePlan[];
  currentPingEncounter?: ADTItem;
  currentPing?: ADTEvent;
  activeCohorts?: Cohort[];
  providers?: Provider[];
  practices?: Practice[];
  primaryCareProvider?: Provider;
  primaryCareProviderCohort?: Cohort;
}

export interface Provider {
  id: number;
  firstName: string;
  lastName: string;
  title: string;
  npi?: string;
  telephone: string;
  email: string;
  providerType: string;
  practiceId: number;
}

export interface Practice {
  practiceId: number;
  name: string;
  address: Address;
  telephone: string;
  fax: string;
  email: string;
  timeZone: string;
  organizationId: number;
  organizationName: string;
}

export interface Cohort {
    id: number;
    name: string;
    groupType: string;
    uuid: string;
    parentId: number;
    parentGroupName: string;
    parentGroupType: string;
    address: Address;
    telephone: string;
    fax: string;
    email: string;
    timeZone: string;
    organizationId: number;
    organizationName: string;
}

export interface ClientData {
  photo?: string;
}

export interface ADTAdmittingFacility {
  groupId: number;
  npi?: string;
  name?: string;
  address?: Address;
  type?: string;
  phoneNumber?: string;
}

export interface CareTeamProvider {
  firstName?: string;
  lastName?: string;
  honorific?: string;
  npi?: string;
  role?: string;
}

export interface ADTItem {
  sdoh?: SdohEntry[];
  admittedFrom?: string;
  encounterId?: string;
  isOpen?: boolean;
  admittingFacility?: ADTAdmittingFacility;
  dischargeDisposition?: string,
  attendingProvider?: {
    firstName?: string,
    lastName?: string,
    honorific?: string,
    email?: string,
    phone?: string,
    role?: string,
  },
  pcp?: {
    firstName?: string,
    lastName?: string,
    honorific?: string,
    npi?: string,
    role?: string,
  },
  careTeamProviders?: CareTeamProvider[];
  diagnoses: Diagnosis[];
  bihProcedures?: BihProcedure[];
  events: ADTEvent[];
  patientLocation?: {
    bed?: string,
    pointOfCare?: string,
    room?: string,
  },
  dischargeSummaryInfo?: DischargeInfo;
  isManual: boolean;
  admittingFacilityPublicPatientId: string;
}

export interface SdohEntry {
  question: string;
  answer: string;
}

export interface ClientProfileUpdate {
  photo?: Blob;
  firstName: string;
  lastName: string;
  middleName?: string;
  dateOfBirth: string;
  gender: string;
  ssn?: string;
  setting?: string;
  phoneType?: string;
  phone?: string;
  address?: Partial<Address>;
  extendedDemographics?: ExtendedDemographics;
  insurancePlans?: InsurancePlan[];
  clientId?: string|null;
}

export type DischargeInfo = {
  uuid: string;
  hasDischargeDiagnosis: boolean;
  hasReasonForVisit: boolean;
  hasHospitalCourse: boolean;
  hasDischargeInstructions: boolean;
  hasDischargeMedication: boolean;
  hasUpcomingEncounters: boolean;
  hasTests: boolean;
  type: string;
  encounterId: string;
  receivedDate: string;
};

export type DischargeEvent = { status: string; facility: { name: string }; eventDate: string };

export interface ClientEncounter {
  type: EncounterType;
  date?: string;
  isPart2?: boolean;
  content: { [key: string]: any };
  careJourneys?: CareJourneyResponse[];
  status: string;
}

export interface ClientProfileResponse {
  status: JobStatus;
  patientProfileJobResult?: JobResult;
  adtJobResult?: JobResult;
  openBedsReferralJobResult?: JobResult;
  openBedsCrisisJobResult?: JobResult;
  justiceInvolvedJobResult?: JobResult;
  findHelpSdohJobResult?: JobResult;
  patientProfileResponse?: ClientProfile;
  encounters?: ClientEncounter[];
  isRestrictedView?: boolean;
}

